import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { EMPTY, Observable, Subscription, TimeoutError, exhaustMap, first, of, switchMap, timeout, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  KI_ANFRAGE_FUNKTION,
  KI_SPERRTEXT_KEYS,
  KI_STATUS_GRUENDE,
  KiAnfrage,
  KiAuftrag,
  KiRunZustand,
  KiStatusGrund,
  KiVerbindungInfo,
} from '../models/ki.model';
import { KiHinweisService } from './ki-hinweis.service';
import { KiStatusService } from './ki-status.service';

const POLL_INTERVALL_MS = 2000;
const ZEITLIMIT_MS = 60_000;
const ALLGEMEIN = 'ki.fehler.allgemein';
const ZEITUEBERSCHREITUNG = 'ki.fehler.zeitueberschreitung';

const PIPELINE_LOG_NICHT_LESBAR = 'PIPELINE_LOG_NICHT_LESBAR';

const FEHLERTEXT_KEYS = new Map<string, string>([
  ['ANBIETER_NICHT_ERREICHBAR', 'ki.fehler.anbieter'],
  ['ZEITUEBERSCHREITUNG', ZEITUEBERSCHREITUNG],
  ['MASKIERUNG_FEHLGESCHLAGEN', 'ki.fehler.maskierung'],
  [PIPELINE_LOG_NICHT_LESBAR, 'ki.fehler.pipelineLog'],
]);

/**
 * Bis zur Migration für den Fehlercode endet ein nicht lesbares Pipeline-Log unter `UNBEKANNT` wie eine unlesbare
 * KI-Antwort; unterschieden wird am festen `grund` (`KiAusschnitteNichtLesbarException`), nicht am Text.
 */
const PIPELINE_LOG_GRUND_KEYS = new Map<string, string>([
  [PIPELINE_LOG_NICHT_LESBAR, 'ki.fehler.pipelineLog'],
  ['GITHUB_TOKEN_UNGUELTIG', 'ki.fehler.githubTokenUngueltig'],
  ['GITHUB_REPO_KEIN_ZUGRIFF', 'ki.fehler.githubRepoKeinZugriff'],
]);

/** Fachliche Ablehnungen des Starts (422), die einen eigenen Text haben. */
const ABLEHNUNG_KEYS = new Map<string, string>([['KI_LAUF_NICHT_FEHLGESCHLAGEN', 'ki.fehler.laufNichtFehlgeschlagen']]);

function statusGrund(grund: unknown): KiStatusGrund | undefined {
  return KI_STATUS_GRUENDE.find((bekannt) => bekannt === grund);
}

function sperrtextKey(grund: unknown): string {
  const bekannt = statusGrund(grund);
  return bekannt ? KI_SPERRTEXT_KEYS[bekannt] : ALLGEMEIN;
}

/**
 * Gemeinsamer Ablauf jeder KI-Anfrage (useKiRun, page-ki.jsx:102-107): Erst-Hinweis, POST, Abfrage
 * alle 2 s bis zum Abschluss. Je Komponente bereitstellen (`providers`), damit ihr Ende die Abfrage
 * beendet; die Anfrage selbst läuft im Backend weiter. Den Hinweis rendert die Komponente aus `hinweis`.
 */
@Injectable()
export class KiRunService {
  private readonly http = inject(HttpClient);
  private readonly kiHinweis = inject(KiHinweisService);
  private readonly kiStatus = inject(KiStatusService);
  private readonly base = `${environment.apiBaseUrl}/api/v1/projects`;

  private readonly zustandSignal = signal<KiRunZustand>({ art: 'bereit' });
  private readonly hinweisSignal = signal<KiVerbindungInfo | null>(null);
  readonly zustand = this.zustandSignal.asReadonly();
  /** Verbindung für den Erst-Nutzungs-Hinweis, solange er offen ist. */
  readonly hinweis = this.hinweisSignal.asReadonly();

  private auftrag?: KiAuftrag;
  /** Getrennt vom Lauf: eine schon bekannte Bestätigung meldet sich synchron und startet ihn noch im Abonnieren. */
  private pruefung?: Subscription;
  private lauf?: Subscription;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.pruefung?.unsubscribe();
      this.lauf?.unsubscribe();
    });
  }

  starten(auftrag: KiAuftrag): void {
    if (this.pruefung?.closed === false || this.lauf?.closed === false || this.hinweis()) return;
    this.auftrag = auftrag;
    this.pruefung = this.kiHinweis.bestaetigt().subscribe((bestaetigt) => {
      if (bestaetigt) {
        this.senden(auftrag);
        return;
      }
      const zustand = this.kiStatus.status(auftrag.projectId)();
      const verbindung = zustand.art === 'geladen' ? zustand.status.verbindung : undefined;
      if (verbindung) {
        this.hinweisSignal.set(verbindung);
      } else {
        this.zustandSignal.set({ art: 'fehler', textKey: ALLGEMEIN });
      }
    });
  }

  /**
   * Zeigt eine schon gestellte Anfrage (Absprung aus dem Postfach, `?kiAnfrage=`) ohne neue Anfrage und
   * ohne Erst-Hinweis. Eine Anfrage anderer Art oder zu einem anderen Kontext bleibt unbeachtet.
   */
  laden(projectId: string, anfrageId: string, erwartet: Pick<KiAnfrage, 'art' | 'kontextId'>): void {
    this.lauf?.unsubscribe();
    const url = `${this.base}/${encodeURIComponent(projectId)}/ki/anfragen/${encodeURIComponent(anfrageId)}`;
    this.lauf = this.http
      .get<KiAnfrage>(url)
      .pipe(
        switchMap((anfrage) => {
          if (anfrage.art !== erwartet.art || anfrage.kontextId !== erwartet.kontextId) return EMPTY;
          if (anfrage.status !== 'LAEUFT') return of(anfrage);
          this.zustandSignal.set({ art: 'laeuft' });
          return this.abschluss(url).pipe(timeout({ first: ZEITLIMIT_MS }));
        }),
      )
      .subscribe({
        next: (anfrage) => this.zeigeAbschluss(anfrage),
        error: (fehler: unknown) =>
          this.zustandSignal.set({
            art: 'fehler',
            textKey: fehler instanceof TimeoutError ? ZEITUEBERSCHREITUNG : ALLGEMEIN,
          }),
      });
  }

  erneut(): void {
    if (this.auftrag) this.starten(this.auftrag);
  }

  hinweisVerstanden(): void {
    const auftrag = this.auftrag;
    if (!this.hinweis() || !auftrag) return;
    this.hinweisSignal.set(null);
    this.zustandSignal.set({ art: 'laeuft' });
    this.pruefung = this.kiHinweis.bestaetigen().subscribe({
      next: () => this.senden(auftrag),
      error: () => this.zustandSignal.set({ art: 'fehler', textKey: ALLGEMEIN }),
    });
  }

  hinweisAbgebrochen(): void {
    this.hinweisSignal.set(null);
  }

  private senden(auftrag: KiAuftrag): void {
    this.zustandSignal.set({ art: 'laeuft' });
    const projektBasis = `${this.base}/${encodeURIComponent(auftrag.projectId)}`;
    const projekt = `${projektBasis}/ki`;
    const start = auftrag.start
      ? { url: `${projektBasis}/${auftrag.start.pfad}`, body: auftrag.start.body }
      : { url: `${projekt}/${auftrag.art}`, body: { kontextTyp: auftrag.kontextTyp, kontextId: auftrag.kontextId } };
    this.lauf = this.http
      .post<{ anfrageId: string }>(start.url, start.body)
      .pipe(
        switchMap(({ anfrageId }) => this.abschluss(`${projekt}/anfragen/${encodeURIComponent(anfrageId)}`)),
        timeout({ first: ZEITLIMIT_MS }),
      )
      .subscribe({
        next: (anfrage) => {
          this.zeigeAbschluss(anfrage);
          this.kiStatus.neuLaden(auftrag.projectId);
        },
        error: (fehler: unknown) => {
          this.zustandSignal.set({ art: 'fehler', textKey: this.abgelehnt(auftrag, fehler) });
          this.kiStatus.neuLaden(auftrag.projectId);
        },
      });
  }

  private abschluss(url: string): Observable<KiAnfrage> {
    return timer(POLL_INTERVALL_MS, POLL_INTERVALL_MS).pipe(
      exhaustMap(() => this.http.get<KiAnfrage>(url)),
      first((anfrage) => anfrage.status !== 'LAEUFT'),
    );
  }

  private zeigeAbschluss(anfrage: KiAnfrage): void {
    this.zustandSignal.set(
      anfrage.status === 'FERTIG' ? { art: 'fertig', anfrage } : { art: 'fehler', textKey: this.fehlertext(anfrage) },
    );
  }

  private fehlertext(anfrage: KiAnfrage): string {
    if (anfrage.fehlercode === 'KI_GESPERRT') return sperrtextKey(anfrage.grund);
    if (anfrage.art === 'PIPELINE_ANALYSE' && anfrage.fehlercode === 'UNBEKANNT') {
      return PIPELINE_LOG_GRUND_KEYS.get(anfrage.grund ?? '') ?? ALLGEMEIN;
    }
    return FEHLERTEXT_KEYS.get(anfrage.fehlercode ?? '') ?? ALLGEMEIN;
  }

  /** 409 KI_GESPERRT und jede 403 sperren die Funktion sofort, bis `neuLaden` den Stand des Backends bringt. */
  private abgelehnt(auftrag: KiAuftrag, fehler: unknown): string {
    if (fehler instanceof TimeoutError) return ZEITUEBERSCHREITUNG;
    if (!(fehler instanceof HttpErrorResponse)) return ALLGEMEIN;
    const funktion = KI_ANFRAGE_FUNKTION[auftrag.art];
    if (fehler.status === 403) {
      this.kiStatus.sperren(auftrag.projectId, funktion, 'rechte');
      return KI_SPERRTEXT_KEYS.rechte;
    }
    if (fehler.status === 409 && fehler.error?.code === 'KI_GESPERRT') {
      const grund = statusGrund(fehler.error.grund);
      this.kiStatus.sperren(auftrag.projectId, funktion, grund);
      return sperrtextKey(grund);
    }
    if (fehler.status === 422) return ABLEHNUNG_KEYS.get(fehler.error?.code) ?? ALLGEMEIN;
    return ALLGEMEIN;
  }
}
